import classNames from 'classnames'
import { loader } from 'graphql.macro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LoadingOverlay from '../../../../components/LoadingOverlay'
import Modal from '../../../../components/Modal'
import Select from '../../../../components/Select'
import Spinner from '../../../../components/Spinner'
import { useToast } from '../../../../contexts/Toast'
import { useImperativeQuery } from '../../../../utils/hooks'
import { createCompany } from '../../clients/controversies'
import { validateCnpj } from '../../utils'

interface Props {
  name: string
  companies: Pick<Company, 'name'>[]
  onClose: () => any
  onCreated?: (name: string) => any
}

const peerGroupsQuery = loader('./peerGroupsQuery.graphql')

interface PeerGroupsQueryResult {
  peerGroups: string[]
}

interface Error {
  message: string
  subject: string
}

const CreateCompanyModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(true)
  const [peerGroups, setPeerGroups] = useState<string[]>([])
  const [peerGroupOption, setPeerGroupOption] = useState<SelectOption | null>(
    null
  )
  const [errors, setErrors] = useState<Error[]>([])
  const [pending, setPending] = useState<boolean>(false)

  const nameInputRef = useRef<HTMLInputElement>(null)
  const tickerInputRef = useRef<HTMLInputElement>(null)
  const cnpjInputRef = useRef<HTMLInputElement>(null)

  const { enqueueToast } = useToast()

  const makePeerGroupsQuery = useImperativeQuery(peerGroupsQuery)

  const peerGroupOptions = useMemo(
    () => peerGroups.map((pg) => ({ label: pg, value: pg })),
    [peerGroups]
  )

  useEffect(() => {
    makePeerGroupsQuery()
      .then((res: PeerGroupsQueryResult) => setPeerGroups(res.peerGroups))
      .finally(() => setLoading(false))
  }, [makePeerGroupsQuery])

  const validateForm = () => {
    const errors: Error[] = []
    if (!nameInputRef.current?.value) {
      errors.push({
        message: t('Pages.ThemesReport.nameIsRequired'),
        subject: 'name',
      })
    }

    if (props.companies.some((c) => c.name === nameInputRef.current?.value)) {
      errors.push({
        message: t(
          'Pages.ThemesReport.thereIsAlreadyACompanyRegisteredWithThisName'
        ),
        subject: 'name',
      })
    }

    if (!peerGroupOption) {
      errors.push({
        message: t('Pages.ThemesReport.peerGroupIsMandatory'),
        subject: 'peerGroup',
      })
    }

    if (!validateCnpj(cnpjInputRef.current?.value || '')) {
      errors.push({
        message: t('Pages.ThemesReport.invalidCnpj'),
        subject: 'cnpj',
      })
    }

    setErrors(errors)

    return errors.length === 0
  }

  const handleChangeCnpj = (newCnpj: string) => {
    const digits = newCnpj.replace(/\D/g, '').split('')
    const formatted = digits.slice(0, 2)

    if (digits.length >= 3) {
      formatted.push('.', ...digits.slice(2, 5))
    }

    if (digits.length >= 6) {
      formatted.push('.', ...digits.slice(5, 8))
    }

    if (digits.length >= 9) {
      formatted.push('/', ...digits.slice(8, 12))
    }

    if (digits.length >= 13) {
      formatted.push('-', ...digits.slice(12, 14))
    }

    if (cnpjInputRef.current) {
      cnpjInputRef.current.value = formatted.join('')
    }

    validateForm()
  }

  const handleSubmit = () => {
    if (pending || !validateForm()) {
      return
    }

    const name = nameInputRef.current!.value

    setPending(true)
    createCompany({
      name,
      peerGroup: peerGroupOption!.value,
      ticker: tickerInputRef.current?.value || '',
      cnpj: cnpjInputRef.current?.value || '',
    })
      .then(() => {
        enqueueToast({
          message: `${t('Pages.ThemesReport.companySuccessfullyCreated')} - ${name}`,
          type: 'success',
        })
        props.onCreated?.(name)
        props.onClose()
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ??
          `${t('Commons.anUnexpectedErrorHasOccurred')} ${t('Commons.pleaseTryAgain')}`
        setErrors([{ message: msg, subject: '' }])
      })
      .finally(() => setPending(false))
  }

  return (
    <Modal
      className="relative p-8"
      open={!!props.name}
      onClose={props.onClose}
      closeButton
    >
      {loading && <LoadingOverlay type="local" />}
      <h2 className="text-2xl">{t('Pages.ThemesReport.registerNewCompany')}</h2>
      <p className="mt-4 text-sm italic">
        {t('Pages.ThemesReport.checkCompanyExistsInCompass')}
      </p>
      <p className="text-sm italic">
        {t('Pages.ThemesReport.enableAlertsWithTickerCNPJ')}
      </p>
      <div className="mt-4 grid grid-cols-[max-content_auto] items-center gap-2">
        <span>{t('Commons.name')}:</span>
        <input
          ref={nameInputRef}
          className={classNames(
            'border px-2 h-[38px]',
            errors.some((e) => e.subject === 'name') && 'border-jgp-error-dark'
          )}
          defaultValue={props.name}
          onBlur={validateForm}
        />
        <span>{t('Pages.ThemesReport.peerGroup')}:</span>
        <Select
          color="dark"
          icon={null}
          options={peerGroupOptions}
          value={peerGroupOption}
          onChange={setPeerGroupOption}
        />
        <span>{t('Commons.ticker')}:</span>
        <input
          ref={tickerInputRef}
          className="h-[38px] border px-2"
          placeholder="Ex.: PETR4 BZ EQUITY"
        />
        <span>CNPJ:</span>
        <input
          ref={cnpjInputRef}
          className={classNames(
            'border px-2 h-[38px]',
            errors.some((e) => e.subject === 'cnpj') && 'border-jgp-error-dark'
          )}
          onBlur={(event) => handleChangeCnpj(event.target.value)}
        />
      </div>
      <ul className="mt-2 min-h-[20px] text-sm text-jgp-error-dark">
        {errors.map((error) => (
          <li key={error.message}>{error.message}</li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-x-6">
        <button
          className="flex w-[4.5rem] items-center justify-center text-jgp-success-dark"
          disabled={pending}
          onClick={handleSubmit}
        >
          {pending ? <Spinner size={18} /> : t('Commons.confirm')}
        </button>
        <button
          className="text-jgp-danger-dark"
          disabled={pending}
          onClick={props.onClose}
        >
          {t('Pages.ThemesReport.cancel')}
        </button>
      </div>
    </Modal>
  )
}

export default CreateCompanyModal
